/**
 * Focus trap
 * Set and keep focus between given elements
 * @module FocusTrap
 */
export default class FocusTrap {
	parentContainer: HTMLElement
	firstFocusableElement: HTMLElement
	focusableElements: string
	focusableContent: Array<Element>
	lastFocusableElement: HTMLElement
	firstActiveElementToFocus: HTMLElement | boolean
	lastFocusableElementOriginalValue?: HTMLElement

	/**
	 * Set default variables as properties
	 * @param options Options
	 * @param options.parentContainer - Parent container of the first and last focusable elements
	 * @param options.firstFocusableElement - First DOM node to set focus on
	 * @param options.lastFocusableElement - Last DOM node to be focused before going back to the first one
	 * @param options.firstActiveElementToFocus - Element to focus first if different from the first focusable element
	 */
	constructor({
		parentContainer,
		firstFocusableElement,
		lastFocusableElement,
		firstActiveElementToFocus = false
	}: {
		parentContainer: HTMLElement
		firstFocusableElement: HTMLElement
		lastFocusableElement: HTMLElement
		firstActiveElementToFocus?: HTMLElement | boolean
	}) {
		this.parentContainer = parentContainer
		this.firstFocusableElement = firstFocusableElement

		this.focusableElements =
			'button:not([disabled]), [href]:not(use), input, select, textarea, [tabindex]:not([tabindex="-1"])'
		this.focusableContent = [...this.parentContainer.querySelectorAll(this.focusableElements)]
		this.lastFocusableElement = lastFocusableElement
		this.firstActiveElementToFocus = firstActiveElementToFocus

		this.updateFocusableContent = this.updateFocusableContent.bind(this)
	}

	/**
	 * Initialize the module
	 */
	init() {
		this.addEvents()
		if (this.firstActiveElementToFocus instanceof HTMLElement) {
			this.firstActiveElementToFocus.focus()
		} else {
			this.firstFocusableElement.focus()
		}
	}

	/**
	 * Add event
	 */
	addEvents() {
		document.addEventListener('keydown', this.updateFocusableContent)
	}

	/**
	 * Update focusable elements based on potential disabled elements
	 * @param e Event data
	 */
	updateFocusableContent(e: KeyboardEvent) {
		this.focusableContent = [...this.parentContainer.querySelectorAll(this.focusableElements)]
		const lastFocusableElementFromArray =
			this.focusableContent[this.focusableContent.length - 1]
		this.lastFocusableElementOriginalValue = this.lastFocusableElement

		if (
			!this.focusableContent.includes(this.lastFocusableElement) &&
			lastFocusableElementFromArray instanceof HTMLElement
		) {
			this.lastFocusableElement = lastFocusableElementFromArray
		}

		this.setFocus(e)
	}

	/**
	 * Set and keep focus on element
	 * @param e Event data
	 */
	setFocus(e: KeyboardEvent) {
		if (e.key === 'Tab' || e.keyCode === 9) {
			this.isTabPressed(e)
		}

		if (this.lastFocusableElementOriginalValue) {
			this.lastFocusableElement = this.lastFocusableElementOriginalValue
		}
	}

	/**
	 * Is tab key pressed
	 * @param e Event data
	 */
	isTabPressed(e: KeyboardEvent) {
		if (e.shiftKey) {
			this.focusBackward(e)
		} else {
			this.focusForward(e)
		}
	}

	/**
	 * Focus backward
	 * @param e Event data
	 */
	focusBackward(e: KeyboardEvent) {
		if (document.activeElement === this.firstFocusableElement) {
			e.preventDefault()
			this.lastFocusableElement.focus()
		}
	}

	/**
	 * Focus forward
	 * @param e Event data
	 */
	focusForward(e: KeyboardEvent) {
		if (document.activeElement === this.lastFocusableElement) {
			e.preventDefault()
			this.firstFocusableElement.focus()
		}
	}

	/**
	 * Remove event listener
	 */
	destroy() {
		document.removeEventListener('keydown', this.setFocus)
	}
}
